/**
 * Base garments refusals: the browser half of `apps/backend/src/lib/base-garment-errors.ts`.
 *
 * The server throws a TRPCError whose MESSAGE is `BASE_GARMENT_<KEY>:<json detail>`
 * and whose `cause.code` is `BASE_GARMENT_<KEY>` (registered in
 * FORWARDABLE_CAUSE_CODES). The parsing (cause first, the marker as the fallback,
 * because tRPC 11.7.1 drops wrapped causes on the wire today) is the shared
 * `parseMarkerError`. Operators never see the marker: every key maps to plain
 * words (`baseGarmentErrorText`).
 */
import type { TFunction } from 'i18next';
import { BASE_GARMENT_CODE_TABLE_MAX, BASE_GARMENT_ERROR_KEYS, type BaseGarmentErrorKey } from '@threadheads/shared';
import { parseMarkerError, trpcErrorCode, type MarkerErrorInfo } from '@/lib/trpc-error-utils';

/** `detail` is the RPC's DETAIL (`key`, `code`, `kind`, `existingName`, ...); {} when unreadable. */
export type BaseGarmentErrorInfo = MarkerErrorInfo<BaseGarmentErrorKey>;

/**
 * The refusal behind a failed Base garments call, or null when it is not one.
 * A forwarded cause that is not one of ours wins over a marker; the cause
 * carries the detail spread out and the marker keeps the product code.
 */
export const parseBaseGarmentError = (error: unknown): BaseGarmentErrorInfo | null =>
  parseMarkerError(error, 'BASE_GARMENT_', BASE_GARMENT_ERROR_KEYS);

const text = (value: unknown): string => (typeof value === 'string' ? value : '');

/**
 * The detail key the server puts on a rename (CODE_IN_USE) or "Labelled for"
 * (CLIENT_CONFLICT) refusal when the code has a substitute garment or is one.
 */
export const OWN_SUBSTITUTE_REFUSAL_KEY = 'own_substitute';

/**
 * A rename met a stock row another save holds right now (the SQL takes them
 * NOWAIT and answers P0942 stock with `busy: true`). Not a fact about the
 * code: say "try again", and never lock the field over it.
 */
export const isBusyRefusal = (info: BaseGarmentErrorInfo | null): boolean =>
  info?.key === 'CODE_IN_USE' && info.detail.busy === true;

/**
 * The operator sentence for a refusal. `code` is the product code the caller
 * was working on, used when the detail does not name it.
 */
export function baseGarmentErrorText(
  t: TFunction<'inventory'>,
  info: BaseGarmentErrorInfo,
  fallbackCode = '',
): string {
  const code = text(info.detail.code) || fallbackCode;
  switch (info.key) {
    case 'CODE_TAKEN':
      return t('baseGarments.errors.codeTaken', { code });
    case 'CODE_IN_USE': {
      if (isBusyRefusal(info)) return t('baseGarments.errors.busy', { code });
      const reason = text(info.detail.key);
      // The code is on either side of an own substitute: the server re-keys the rename refusal.
      if (reason === OWN_SUBSTITUTE_REFUSAL_KEY) return t('baseGarments.errors.ownSubstituteRename', { code });
      // BLU-1442 the Codes panel's delete: why the code keeps its last name.
      if (reason === 'skus') {
        const count = typeof info.detail.count === 'number' ? info.detail.count : 0;
        const sample = text(info.detail.sample);
        return sample
          ? t('baseGarments.errors.codeUsedBySkus', { code, count, sku: sample })
          : t('baseGarments.errors.codeUsedBySkusNoSample', { code, count });
      }
      if (reason === 'base_garment') {
        return t('baseGarments.errors.codeIsBaseGarment', { code, name: text(info.detail.baseGarment) });
      }
      if (reason === 'colourless') return t('baseGarments.errors.codeIsColourless', { code });
      if (reason === 'default_size') return t('baseGarments.errors.codeIsDefaultSize', { code });
      if (text(info.detail.field) === 'audience') return t('baseGarments.errors.audienceHasStock', { code });
      // Un-sharing while a client still maps its products to the code's blanks (the SKU guard).
      if (text(info.detail.reason) === 'mappings') return t('baseGarments.errors.unshareMappings', { code });
      if (reason === 'history') return t('baseGarments.identity.inUse.history', { code });
      if (reason === 'designs') return t('baseGarments.identity.inUse.designs', { code });
      if (reason === 'mappings') return t('baseGarments.identity.inUse.mappings', { code });
      return t('baseGarments.identity.inUse.stock', { code });
    }
    case 'CLIENT_CONFLICT':
      if (text(info.detail.key) === OWN_SUBSTITUTE_REFUSAL_KEY) {
        // Garments already in production were built on the code as an own
        // substitute: removing the substitute does not help, packing them does.
        return info.detail.inProduction === true
          ? t('baseGarments.errors.ownSubstituteLabelInProduction', { code })
          : t('baseGarments.errors.ownSubstituteLabel', { code });
      }
      return text(info.detail.key) === 'election'
        ? t('baseGarments.errors.clientConflictElection', { code })
        : t('baseGarments.errors.clientConflictMapping', { code });
    case 'CODE_NAME_TAKEN': {
      const existingName = text(info.detail.existingName);
      const existingCode = text(info.detail.existingCode);
      if (existingName) return t('baseGarments.errors.codeNameTaken', { code: text(info.detail.code), name: existingName });
      if (existingCode) {
        return t('baseGarments.errors.nameHasCode', { name: text(info.detail.name), code: existingCode });
      }
      return t('baseGarments.errors.nameTaken', { name: text(info.detail.name) });
    }
    case 'CLIENT_NOT_CONNECTED':
      return t('baseGarments.errors.clientNotConnected');
    case 'SHAPE_MIX':
      return t('baseGarments.errors.shapeMix', { code });
    case 'SKU_NOT_EDITABLE':
      return t('baseGarments.errors.skuNotEditable');
    case 'RESERVED_RENAME':
      return t('baseGarments.errors.reservedRename');
    case 'CODE_NOT_FOUND':
      // BLU-1442: the code-table entry changed since the panel read it.
      if (text(info.detail.key) === 'entry_changed') {
        return t('baseGarments.errors.entryChanged', { name: text(info.detail.name), code });
      }
      return t('baseGarments.errors.codeNotFound', { code });
    case 'UNKNOWN_CODE':
      return t('baseGarments.errors.unknownCode', { code: text(info.detail.code) });
    case 'CODE_TABLE_FULL':
      return t('baseGarments.errors.codeTableFull', { tableMax: BASE_GARMENT_CODE_TABLE_MAX.toLocaleString() });
    case 'IMPORT_INCONSISTENT': {
      const { total } = importRowProblems(info);
      if (total === 0) return t('baseGarments.errors.importInconsistent');
      const lines = importRowLines(t, info, IMPORT_LINES_IN_SENTENCE, 'sentence');
      return [t('baseGarments.import.rowsTitle', { count: total }), ...lines].join(' ');
    }
    case 'TOO_MANY':
      return t('baseGarments.errors.tooMany');
    case 'RELINK_REFUSED':
      return relinkRefusedText(t, info, code);
    case 'OWN_SUBSTITUTE':
      return ownSubstituteRefusalText(t, info, code);
    default:
      return t('baseGarments.errors.generic');
  }
}

/**
 * A Re-link the database refused (it rolls back, so the old link stays): why, by the
 * decider's status, and the SKU behind it when it names one.
 */
function relinkRefusedText(t: TFunction<'inventory'>, info: BaseGarmentErrorInfo, code: string): string {
  const status = text(info.detail.status);
  const sku = text(info.detail.offendingSku) || text(info.detail.sku);
  switch (status) {
    case 'mixed_shape':
      return sku
        ? t('baseGarments.errors.relinkMixedShapeSku', { code, sku })
        : t('baseGarments.errors.relinkMixedShape', { code });
    case 'needs_review':
      return sku
        ? t('baseGarments.errors.relinkNeedsReviewSku', { code, sku })
        : t('baseGarments.errors.relinkNeedsReview', { code });
    case 'finished_goods':
      return t('baseGarments.errors.relinkFinishedGoods', { code });
    case 'no_blanks':
      return t('baseGarments.errors.relinkNoBlanks', { code });
    case 'no_link':
      return t('baseGarments.errors.relinkNoLink', { code });
    default:
      return t('baseGarments.errors.relinkRefused', { code });
  }
}

/**
 * The Substitute garment editor refused (OWN_SUBSTITUTE, `detail.reason` says
 * why). A pair that no longer fits the two codes means a size or colour
 * changed while the operator was choosing: look again and save again.
 */
function ownSubstituteRefusalText(t: TFunction<'inventory'>, info: BaseGarmentErrorInfo, code: string): string {
  const substituteCode = text(info.detail.substituteCode);
  switch (text(info.detail.reason)) {
    case 'labelled':
      return text(info.detail.side) === 'substitute'
        ? t('baseGarments.errors.ownSubstituteLabelledSub', { code })
        : t('baseGarments.errors.ownSubstituteLabelledMain', { code });
    case 'no_pairs':
      return substituteCode
        ? t('baseGarments.errors.ownSubstituteNoPairs', { code, substitute: substituteCode })
        : t('baseGarments.errors.ownSubstituteNoPairsNoCode', { code });
    case 'same_code':
      return t('baseGarments.errors.ownSubstituteSameCode');
    case 'main_not_in_code':
    case 'sub_not_in_code':
    case 'bad_pairs':
    case 'duplicate_main':
    case 'pairs_without_substitute':
      return t('baseGarments.errors.ownSubstituteChanged');
    case 'too_many':
      return t('baseGarments.errors.tooMany');
    default:
      return t('baseGarments.errors.generic');
  }
}

// ── Upload refusals, per row ──────────────────────────────────────────────

/** Rows named in the one-line refusal (a toast); the upload's check step lists all it got. */
const IMPORT_LINES_IN_SENTENCE = 3;

export interface ImportRowProblem {
  /** The upload row, 1-based, in the order the rows were sent. */
  row: number;
  /** `archived_sku`, `not_a_blank`, `duplicate_row`, `unknown_code`, `<level>_disagrees:<field>`, `bad_*`. */
  problem: string;
  sku?: string;
}

/**
 * An IMPORT_INCONSISTENT refusal's rows: the first 50 the server names, and
 * how many there are in all (`total`, which also covers a detail cut to size).
 */
export function importRowProblems(info: BaseGarmentErrorInfo): { rows: ImportRowProblem[]; total: number } {
  const raw = Array.isArray(info.detail.rows) ? (info.detail.rows as unknown[]) : [];
  const rows: ImportRowProblem[] = [];
  for (const entry of raw) {
    if (!entry || typeof entry !== 'object') continue;
    const { row, problem, sku } = entry as Record<string, unknown>;
    if (typeof row !== 'number' || typeof problem !== 'string') continue;
    rows.push({ row, problem, ...(typeof sku === 'string' && sku !== '' ? { sku } : {}) });
  }
  const total = typeof info.detail.total === 'number' && info.detail.total >= rows.length ? info.detail.total : rows.length;
  return { rows, total };
}

/**
 * The refusal with each row named by its FILE line. The server numbers the
 * rows it was sent (1-based); the upload sends only new and changed rows, so
 * `rowLines` (the diff's file line per sent row, same order) maps them back.
 * A number with no line keeps its own.
 */
export function withFileLines(info: BaseGarmentErrorInfo, rowLines: readonly number[]): BaseGarmentErrorInfo {
  if (!Array.isArray(info.detail.rows)) return info;
  const rows = (info.detail.rows as unknown[]).map((entry) => {
    if (!entry || typeof entry !== 'object') return entry;
    const { row } = entry as Record<string, unknown>;
    const line = typeof row === 'number' ? rowLines[row - 1] : undefined;
    return line === undefined ? entry : { ...(entry as Record<string, unknown>), row: line };
  });
  return { ...info, detail: { ...info.detail, rows } };
}

/** What is wrong with one row, in words. */
export function importProblemText(t: TFunction<'inventory'>, problem: ImportRowProblem): string {
  const sku = problem.sku ?? '';
  if (problem.problem === 'archived_sku') return t('baseGarments.import.problem.archivedSku', { sku });
  if (problem.problem === 'not_a_blank') return t('baseGarments.import.problem.notABlank', { sku });
  if (problem.problem === 'duplicate_row') return t('baseGarments.import.problem.duplicateRow');
  if (problem.problem === 'unknown_code') return t('baseGarments.import.problem.unknownCode');
  const disagrees = /^(code|size|colour)_disagrees:/.exec(problem.problem);
  if (disagrees) return t(`baseGarments.import.problem.disagrees.${disagrees[1] as 'code' | 'size' | 'colour'}`);
  return t('baseGarments.import.problem.unreadable');
}

/**
 * One line per named row ("Row 12: TEE-M-BK is archived. ..."), up to `limit`,
 * then the rest of `total`: "and N more" closing a list, "Plus N more rows."
 * closing a sentence.
 */
export function importRowLines(
  t: TFunction<'inventory'>,
  info: BaseGarmentErrorInfo,
  limit = Number.POSITIVE_INFINITY,
  form: 'list' | 'sentence' = 'list',
): string[] {
  const { rows, total } = importRowProblems(info);
  const shown = rows.slice(0, limit);
  const lines = shown.map((problem) => t('baseGarments.import.row', { row: problem.row, problem: importProblemText(t, problem) }));
  const more = total - shown.length;
  if (more > 0) {
    lines.push(form === 'list' ? t('baseGarments.andMore', { count: more }) : t('baseGarments.import.moreRows', { count: more }));
  }
  return lines;
}

/**
 * The sentence for any failed call: the refusal's words; a permission refusal
 * (the router's FORBIDDEN, or the SQL's 42501 the backend maps to it) as
 * such; else the generic line.
 */
export function anyBaseGarmentErrorText(t: TFunction<'inventory'>, error: unknown, fallbackCode = ''): string {
  const info = parseBaseGarmentError(error);
  if (info) return baseGarmentErrorText(t, info, fallbackCode);
  if (trpcErrorCode(error) === 'FORBIDDEN') return t('baseGarments.errors.forbidden');
  return t('baseGarments.errors.generic');
}
